// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext();

// Parse admin emails once at module load
const adminEmails = process.env.REACT_APP_ADMIN_EMAILS 
  ? process.env.REACT_APP_ADMIN_EMAILS.split(',').map(email => email.trim())
  : [];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleUserSession = async (session) => {
    if (session?.user) {
      try {
        // First get existing profile
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
          // PGRST116 is "not found" error, which is expected for new users
          console.error('Error fetching existing profile:', fetchError);
        }

        // Only update empty fields with auth metadata
        const updates = {
          id: session.user.id,
          full_name: existingProfile?.full_name || session.user.user_metadata?.full_name || '',
          phone_number: existingProfile?.phone_number || session.user.phone || '',
          email: session.user.email,
          avatar_url: existingProfile?.avatar_url || session.user.user_metadata?.avatar_url || ''
        };

        // Upsert merged data
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert(updates);

        if (upsertError) {
          console.error('Error upserting profile:', upsertError);
          // Continue execution - we'll try to fetch whatever profile exists
        }

        // Fetch final profile
        const { data: profile, error: finalFetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (finalFetchError) {
          console.error('Error fetching final profile:', finalFetchError);
          // Set user without profile data if profile fetch fails
          setUser(session.user);
        } else {
          setUser({ ...session.user, profile });
        }

        // Check against all admin emails
        setIsAdmin(adminEmails.includes(session.user.email));
      } catch (error) {
        console.error('Unexpected error in handleUserSession:', error);
        // Set basic user data even if profile operations fail
        setUser(session.user);
        setIsAdmin(adminEmails.includes(session.user.email));
      }
    } else {
      setUser(null);
      setIsAdmin(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleUserSession(session);
    });

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        handleUserSession(session);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const value = {
    user,
    isAdmin,
    loading,
    signInWithGoogle: async () => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin }
      });
      if (error) throw error;
    },
    signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);